import React from 'react';

import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import classNames from 'classnames';

import classes from './MetricTrendValue.module.scss';
import { getValueOrDash } from '../../../../../../shared/utils/stringUtils/getValueOrDash/getValueOrDash';
import { getIsNullish } from '../../../../../../shared/utils/jsUtils';

type MetricTrendValueProps = {
  value: number | null;
  /**
   * Which way the arrow points when `value` is positive.
   *
   * `fatLoss*` fields are inverted — `fatLossPercentage: 22.22` means 22.22% of fat was
   * LOST, so the fat itself went down: `'down'`. `muscleGain*` is direct: `'up'`.
   */
  arrowOnPositive: 'up' | 'down';
  /** Rendered right after the value, e.g. '%'. Omit for `lbs` — it lives in the column title. */
  unit?: string;
};

/**
 * Leaderboard metric value prefixed with a trend arrow.
 *
 * The sign is stripped from the value — the arrow is the only carrier of direction.
 */
const MetricTrendValue = ({ value, arrowOnPositive, unit }: MetricTrendValueProps) => {
  const withoutTrend = getIsNullish(value) || value === 0;

  // every field is named after its desirable direction (fatLoss*, muscleGain*),
  // so a positive value always means the participant did well
  const isPositive = !withoutTrend && value > 0;

  const isArrowUp = isPositive === (arrowOnPositive === 'up');

  const TrendIcon = isArrowUp ? ArrowUpwardRoundedIcon : ArrowDownwardRoundedIcon;

  const trendColor = isPositive ? 'var(--success-500)' : 'var(--error-500)';

  const absoluteValue = getIsNullish(value) ? value : Math.abs(value);

  return (
    <div className={classes.container}>
      <TrendIcon
        fontSize="small"
        htmlColor={trendColor}
        className={classNames({
          [classes.hiddenTrend]: withoutTrend,
        })}
      />
      <span>{getValueOrDash(absoluteValue, unit)}</span>
    </div>
  );
};

export { MetricTrendValue };
